/**
 * ReviewStoryCard
 *
 * Renders one generated Jira story inside a review list. Composes
 * ReviewItemCard as its outer shell and accepts all story fields as props.
 *
 * Props
 * ─────
 * story              object     – Story data (see shape below)
 * onToggle           fn         – (id) => void — proxied to ReviewItemCard
 * onEdit             fn         – (story) => void — called when Edit is clicked
 * disabled           bool       – Passed through to ReviewItemCard
 * linkedProjects     array      – [{ linked_project_id, linked_project_key, linked_project_name }]
 *                                 When provided, renders the Jira project selector.
 * onProjectChange    fn         – (projectId) => void
 * suggestedLabels    string[]   – AI-suggested labels shown as dismissible chips
 * onAcceptLabel      fn         – (label) => void
 * onDismissLabel     fn         – (label) => void
 *
 * Story shape
 * ───────────
 * {
 *   id:                 string
 *   selected:           bool
 *   type:               string       ('Story' | 'Epic' | …)
 *   issueType:          string       alias for type, used when type is absent
 *   title:              string
 *   description:        string
 *   epic:               string
 *   priority:           string
 *   labels:             string[]
 *   acceptanceCriteria: string[]
 *   similarExists:      bool
 *   jiraProjectId:      string|null
 * }
 */

import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import EditIcon from '@mui/icons-material/Edit';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Box,
  Button,
  Chip,
  FormControl,
  MenuItem,
  OutlinedInput,
  Select,
  Typography,
} from '@mui/material';
import ReviewItemCard from './ReviewItemCard';

// ── JiraProjectSelector ───────────────────────────────────────────────────────

const JiraProjectSelector = ({ value, linkedProjects = [], onChange, disabled = false, storyId, isMissing = false }) => {
  if (!linkedProjects.length) return null;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 1 }}>
      <Typography variant="caption" color="text.secondary" fontWeight={500}>
        Jira Project
      </Typography>
      <FormControl size="small">
        <Select
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          input={<OutlinedInput />}
          IconComponent={KeyboardArrowDownIcon}
          displayEmpty
          disabled={disabled}
          inputProps={{ 'aria-label': `Jira project for story ${storyId}` }}
          renderValue={(val) =>
            val
              ? (linkedProjects.find((p) => p.linked_project_id === val)?.linked_project_key ?? val)
              : <span style={{ color: '#9e9e9e' }}>Select</span>
          }
          sx={{
            fontSize: '12px',
            height: '28px',
            minWidth: 150,
            bgcolor: isMissing ? '#FFFBEB' : 'transparent',
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: isMissing ? '#FCD34D' : '#ced4da',
            },
          }}
        >
          <MenuItem value="" disabled>
            <span style={{ color: '#9e9e9e', fontSize: '12px' }}>Select project</span>
          </MenuItem>
          {linkedProjects.map((proj) => (
            <MenuItem key={proj.linked_project_id} value={proj.linked_project_id} sx={{ fontSize: '12px' }}>
              {proj.linked_project_key}
              {proj.linked_project_name ? ` — ${proj.linked_project_name}` : ''}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

// ── SuggestedLabels ───────────────────────────────────────────────────────────

const SuggestedLabels = ({ labels = [], onAccept, onDismiss }) => {
  if (!labels.length) return null;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mt: 0.75 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
        <AutoAwesomeIcon sx={{ fontSize: 13, color: 'primary.main' }} />
        <Typography variant="caption" fontWeight={600} color="primary.main" sx={{ fontSize: '11px' }}>
          Suggested:
        </Typography>
      </Box>
      {labels.map((lbl) => (
        <Chip
          key={lbl}
          label={lbl}
          size="small"
          variant="outlined"
          color="primary"
          onClick={() => onAccept(lbl)}
          onDelete={() => onDismiss(lbl)}
          sx={{ fontSize: '11px', height: 22, cursor: 'pointer' }}
        />
      ))}
    </Box>
  );
};

// ── ReviewStoryCard ───────────────────────────────────────────────────────────

const ReviewStoryCard = ({
  story,
  onToggle,
  onEdit,
  disabled = false,
  linkedProjects = [],
  onProjectChange,
  suggestedLabels = [],
  onAcceptLabel,
  onDismissLabel,
}) => (
  <ReviewItemCard
    id={story.id}
    selected={story.selected}
    onToggle={onToggle}
    disabled={disabled}
    hasWarning={story.selected && !story.jiraProjectId && linkedProjects.length > 0}
    warningLabel="Jira project required"
    ariaLabel={`Select: ${story.title}`}
  >
    {/* Header row: type chip + similar-exists badge + Edit button */}
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mb: 0.75,
        flexWrap: 'wrap',
        gap: 0.75,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Chip
          label={story.issueType || story.type}
          size="small"
          sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600, bgcolor: 'success.light', color: 'success.main' }}
        />
        {story.similarExists && (
          <Chip
            label="⚠ Similar exists"
            size="small"
            icon={<WarningAmberIcon sx={{ fontSize: '14px !important' }} />}
            sx={{ height: 20, fontSize: '0.7rem', bgcolor: 'warning.light', color: 'warning.dark' }}
          />
        )}
      </Box>

      {onEdit && (
        <Button
          size="small"
          startIcon={<EditIcon sx={{ fontSize: '12px !important' }} />}
          onClick={(e) => { e.stopPropagation(); onEdit(story); }}
          disabled={disabled}
          sx={{ fontSize: '0.75rem', textTransform: 'none', flexShrink: 0 }}
        >
          Edit
        </Button>
      )}
    </Box>

    {/* Title */}
    <Typography variant="body1" fontWeight={600} sx={{ mb: 0.75 }}>
      {story.title}
    </Typography>

    {/* Description */}
    {story.description && (
      <Typography
        variant="body2"
        color="text.primary"
        sx={{
          display: '-webkit-box',
          WebkitLineClamp: 3,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          lineHeight: 1.5,
          mb: 0.75,
        }}
      >
        {story.description}
      </Typography>
    )}

    {/* Epic / Priority meta */}
    {(story.epic || story.priority) && (
      <Box sx={{ display: 'flex', gap: 3, mb: 0.75, flexWrap: 'wrap' }}>
        {story.epic && (
          <Typography variant="body2" color="text.secondary">
            <Box component="span" fontWeight={600}>Epic:</Box> {story.epic}
          </Typography>
        )}
        {story.priority && (
          <Typography variant="body2" color="text.secondary">
            <Box component="span" fontWeight={600}>Priority:</Box> {story.priority}
          </Typography>
        )}
      </Box>
    )}

    {/* Labels — uncomment once label UX is finalised
    {story.labels?.length > 0 && (
      <Box sx={{ mb: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          <Box component="span" fontWeight={600}>Labels:</Box>{' '}
          {story.labels.join(', ')}
        </Typography>
      </Box>
    )} */}

    {/* Suggested labels — uncomment once label UX is finalised
    {suggestedLabels.length > 0 && (
      <SuggestedLabels
        labels={suggestedLabels}
        onAccept={onAcceptLabel}
        onDismiss={onDismissLabel}
      />
    )} */}

    {/* Acceptance criteria */}
    {story.acceptanceCriteria?.length > 0 && (
      <Box sx={{ mt: 0.5 }}>
        <Typography
          variant="caption"
          fontWeight={700}
          color="text.secondary"
          sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
        >
          Acceptance Criteria
        </Typography>
        <Box component="ul" sx={{ m: 0, pl: 2 }}>
          {story.acceptanceCriteria.map((ac, i) => (
            <Typography key={i} component="li" variant="body2" color="text.primary" sx={{ mt: 0.25 }}>
              {ac}
            </Typography>
          ))}
        </Box>
      </Box>
    )}

    {/* Jira project selector */}
    <JiraProjectSelector
      value={story.jiraProjectId ?? null}
      linkedProjects={linkedProjects}
      onChange={onProjectChange}
      storyId={story.id}
      isMissing={story.selected && !story.jiraProjectId && linkedProjects.length > 0}
    />
  </ReviewItemCard>
);

export default ReviewStoryCard;